<template>
  <a-row :gutter="24">
    <a-col :md="24">
      <a-card :style="cardStyle" :bordered="false">
        <!-- 门店后台模式横幅（进入后台切换的门店身份，可返回总店） -->
        <div v-if="proxyMode" class="proxy-banner">
          <a-icon type="info-circle" /> 已进入门店后台（{{ proxyStoreName }}），当前总店标签页同步切换为门店身份
          <a class="proxy-back" @click="handleBackToHq">返回总店</a>
        </div>
        <!-- 顶部 Tab：门店列表/回收站（克隆云贝 #/shoplist） -->
        <a-tabs v-model="activeTab" @change="onTabChange">
          <a-tab-pane tab="门店列表" key="list" />
          <a-tab-pane tab="回收站" key="recycle" />
        </a-tabs>
        <!-- 查询区域（克隆云贝筛选：按关键字/按门店类别/按门店区域） -->
        <div class="table-page-search-wrapper">
          <a-form layout="inline" @keyup.enter.native="searchQuery">
            <a-row :gutter="24">
              <a-col :md="6" :sm="24">
                <a-form-item label="按关键字：">
                  <a-input placeholder="请输入关键字搜索" v-model="queryParam.keyword" style="width: 180px" />
                </a-form-item>
              </a-col>
              <a-col :md="6" :sm="24">
                <a-form-item label="按门店类别：">
                  <a-select placeholder="请选择" v-model="queryParam.storeType" style="width: 140px" allow-clear @change="searchQuery">
                    <a-select-option value="1">直营店</a-select-option>
                    <a-select-option value="2">加盟店</a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
              <a-col :md="6" :sm="24">
                <a-form-item label="按门店区域：">
                  <a-select placeholder="请选择" v-model="queryParam.provinceId" style="width: 140px" allow-clear @change="searchQuery">
                    <a-select-option v-for="p in provinceOptions" :key="p.value" :value="p.value">{{ p.label }}</a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
              <a-col :md="6" :sm="24">
                <span style="float: left;overflow: hidden;" class="table-page-search-submitButtons">
                  <a-button type="primary" @click="searchQuery">查询</a-button>
                  <a-button style="margin-left: 8px" @click="searchReset">重置</a-button>
                </span>
              </a-col>
            </a-row>
          </a-form>
        </div>
        <!-- 操作按钮区域 -->
        <div class="table-operator" style="margin-top: 5px">
          <a-button v-if="activeTab === 'list' && btnEnableList.indexOf(1)>-1" @click="handleAdd" type="primary" icon="plus">新增门店</a-button>
          <span v-if="activeTab === 'list'" class="field-tip" style="margin-left: 10px">新增门店将创建独立租户与主账号（仅总店可操作）</span>
        </div>
        <!-- table区域 -->
        <a-table
          ref="table"
          size="middle"
          bordered
          rowKey="id"
          :columns="columns"
          :dataSource="dataSource"
          :pagination="ipagination"
          :loading="loading"
          @change="handleTableChange">
          <template slot="nameSlot" slot-scope="text, record">
            <div class="store-name">
              <span>{{ record.storeName || record.name }}</span>
              <!-- 总店标识（is_main='1'，子租户列表扩展行回带） -->
              <a-tag v-if="String(record.isMain) === '1'" color="blue">总店</a-tag>
            </div>
          </template>
          <template slot="statusSlot" slot-scope="text, record">
            <a-tag :color="String(record.status) === '1' ? 'green' : 'red'">{{ String(record.status) === '1' ? '启用' : '停用' }}</a-tag>
          </template>
          <template slot="modelSlot" slot-scope="text, record">
            <div class="store-model">
              <!-- 商品模式列（克隆云贝：模式一/二/三 + tag），点击"修改"弹商品模式弹窗 -->
              <span v-if="String(record.goodsModel) === '1'">模式一：<a-tag color="green">同步总店商品</a-tag></span>
              <span v-else-if="String(record.goodsModel) === '2'">模式二：<a-tag>普通商品权限</a-tag></span>
              <span v-else>模式三：<a-tag color="red">超级商品权限</a-tag></span>
              <a class="model-edit" @click="handleGoodsModel(record)">修改</a>
            </div>
          </template>
          <span slot="action" slot-scope="text, record">
            <template v-if="activeTab === 'list'">
              <!-- 进入后台：门店身份切换（一次性授权码换临时会话，横幅可返回总店） -->
              <a-button size="small" style="margin-right: 8px" @click="handleEnterBackend(record)">进入后台</a-button>
              <a @click="handleEdit(record)">编辑</a>
              <a-divider type="vertical" />
              <a v-if="btnEnableList.indexOf(1)>-1" @click="handleDelete(record)">删除</a>
            </template>
            <template v-else>
              <a @click="handleRestore(record)">恢复</a>
              <a-divider type="vertical" />
              <a v-if="btnEnableList.indexOf(1)>-1" @click="handlePurge(record)">彻底删除</a>
            </template>
          </span>
        </a-table>
        <!-- 商品模式弹窗（三模式 + 红色警示文案照抄 spec 0.5） -->
        <goods-model-modal ref="goodsModelModal" @ok="loadData" />
        <!-- 新增门店弹窗（建租户事务：名称/直营加盟/主账号登录名/初始密码） -->
        <a-modal
          title="新增门店"
          :visible="addVisible"
          :confirmLoading="addSaving"
          :maskClosable="false"
          @ok="handleAddOk"
          @cancel="addVisible = false">
          <a-form class="add-form">
            <a-form-item label="门店简称" required :labelCol="{ span: 5 }" :wrapperCol="{ span: 18 }">
              <a-input v-model="addForm.name" placeholder="门店简称（招牌字号，区别于营业执照企业全称）" />
            </a-form-item>
            <a-form-item label="门店类别" required :labelCol="{ span: 5 }" :wrapperCol="{ span: 18 }">
              <a-radio-group v-model="addForm.storeType">
                <a-radio value="1">直营店</a-radio>
                <a-radio value="2">加盟店</a-radio>
              </a-radio-group>
            </a-form-item>
            <a-form-item label="老板姓名" required :labelCol="{ span: 5 }" :wrapperCol="{ span: 18 }">
              <a-input v-model="addForm.bossName" placeholder="门店老板真实姓名（主账号姓名）" />
            </a-form-item>
            <a-form-item label="主账号登录名" required :labelCol="{ span: 5 }" :wrapperCol="{ span: 18 }">
              <a-input v-model="addForm.loginName" autocomplete="off" placeholder="门店后台主账号登录名" />
              <div class="field-tip">账号请不要用中文</div>
            </a-form-item>
            <a-form-item label="初始密码" required :labelCol="{ span: 5 }" :wrapperCol="{ span: 18 }">
              <a-input v-model="addForm.initPassword" type="password" autocomplete="off" placeholder="主账号初始密码" />
              <div class="field-tip">密码至少要有数字和小写字母，并且长度至少6位</div>
            </a-form-item>
          </a-form>
          <a-alert type="info" show-icon message="创建后将生成独立租户与主账号" style="margin-top: 4px" />
        </a-modal>
      </a-card>
    </a-col>
  </a-row>
</template>
<script>
import { pcaa } from 'area-data'
import md5 from 'md5'
import { getAction, deleteAction, postAction } from '@/api/manage'
import { JeecgListMixin } from '@/mixins/JeecgListMixin'
import { mixinDevice } from '@/utils/mixin'
import GoodsModelModal from './GoodsModelModal'

/**
 * 门店列表页（门店租户化阶段一：子租户列表 + 新增门店=建租户事务 + 编辑跳门店信息页）
 * API 契约（Task 3.2 后端）：
 *   GET    /api/plugin/dining/stores?page&pageSize&keyword&storeType&provinceId&recycleBin
 *          rows：storeName/storeType/status/loginName(主账号)/goodsModel/businessStatus/createTime
 *                + id(jsh_store id)/tenantRefId（编辑跳 StoreInfo 用 jsh_store id）
 *   POST   /api/plugin/dining/stores          body {name,storeType,loginName,initPassword,...}（建租户事务，仅总店）
 *   DELETE /api/plugin/dining/stores/{id}          软删（进回收站）
 *   POST   /api/plugin/dining/stores/{id}/restore  恢复
 *   DELETE /api/plugin/dining/stores/{id}/purge    彻底删除
 * "进入后台"（跨租户代理上下文）为阶段二能力，当前 disabled 占位
 */
const STORE_PREFIX = '/api/plugin/dining/stores'
// 门店信息页路由（与菜单 SQL 对应：url=/plugin/dining/store-info，component=plugin/dining/store/StoreInfo）
const STORE_INFO_PATH = '/plugin/dining/store-info'

export default {
  name: 'DiningStoreList',
  mixins: [JeecgListMixin, mixinDevice],
  components: {
    GoodsModelModal
  },
  data() {
    return {
      // 按钮权限匹配键（与菜单 2901 url 一致，initActiveBtnStr 按 urlPath 匹配登录缓存的 winBtnStrList，
      // 缺失则 btnEnableList 恒空 → 新增/删除按钮全部隐藏）
      urlPath: '/plugin/dining/store',
      queryParam: {},
      activeTab: 'list',
      provinceOptions: [],
      ipagination: {
        pageSizeOptions: ['10', '20', '30', '100', '200']
      },
      columns: [
        {
          title: '#',
          dataIndex: '',
          key: 'rowIndex',
          width: 60,
          align: 'center',
          customRender: function(t, r, index) {
            return parseInt(index) + 1
          }
        },
        { title: '门店简称', dataIndex: 'storeName', width: 220, align: 'left', scopedSlots: { customRender: 'nameSlot' } },
        { title: '门店类别', dataIndex: 'storeType', width: 100, align: 'center',
          customRender: t => (String(t) === '2' ? '加盟店' : '直营店') },
        { title: '状态', dataIndex: 'status', width: 80, align: 'center', scopedSlots: { customRender: 'statusSlot' } },
        { title: '主账号', dataIndex: 'loginName', width: 140, align: 'center', customRender: t => t || '-' },
        { title: '商品模式', dataIndex: 'goodsModel', width: 200, align: 'center', scopedSlots: { customRender: 'modelSlot' } },
        { title: '操作', dataIndex: 'action', width: 220, align: 'center', scopedSlots: { customRender: 'action' } }
      ],
      // 新增门店弹窗（建租户事务：名称/直营加盟/主账号登录名/初始密码）
      addVisible: false,
      addSaving: false,
      addForm: { name: '', storeType: '1', loginName: '', initPassword: '' },
      // 门店后台模式（dining-proxy-backup 存在=进入后台切换的身份）
      proxyMode: false,
      proxyStoreName: ''
    }
  },
  created() {
    // 门店区域筛选（省下拉，area-data pcaa 与门店信息页省市区同源）
    const provinces = pcaa['86'] || {}
    this.provinceOptions = Object.keys(provinces).map(code => ({ value: code, label: provinces[code] }))
    this.initProxyMode()
  },
  activated() {
    // keep-alive 多页签：从门店信息页保存返回后刷新列表
    this.loadData()
  },
  methods: {
    // 后端契约：GET /stores（page/pageSize/keyword/storeType/provinceId/recycleBin），覆写 mixin 的 loadData
    loadData(arg) {
      if (arg === 1) {
        this.ipagination.current = 1
      }
      this.loading = true
      const params = {
        page: this.ipagination.current,
        pageSize: this.ipagination.pageSize
      }
      if (this.queryParam.keyword) {
        params.keyword = this.queryParam.keyword
      }
      if (this.queryParam.storeType) {
        params.storeType = this.queryParam.storeType
      }
      if (this.queryParam.provinceId) {
        params.provinceId = this.queryParam.provinceId
      }
      if (this.activeTab === 'recycle') {
        params.recycleBin = true
      }
      getAction(STORE_PREFIX, params).then(res => {
        if (res.code === 200) {
          this.dataSource = res.data.rows || []
          this.ipagination.total = res.data.total || 0
        } else {
          this.$message.warning(res.message)
        }
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    searchReset() {
      this.queryParam = {}
      this.loadData(1)
    },
    onTabChange() {
      this.loadData(1)
    },
    // 新增门店：弹窗（建租户事务，POST /stores body {name,storeType,loginName,initPassword}）
    handleAdd() {
      this.addForm = { name: '', storeType: '1', bossName: '', loginName: '', initPassword: '' }
      this.addVisible = true
    },
    handleAddOk() {
      const f = this.addForm
      if (!f.name || !f.name.trim()) {
        this.$message.warning('请输入门店名称')
        return
      }
      if (!f.bossName || !f.bossName.trim()) {
        this.$message.warning('请输入老板姓名')
        return
      }
      if (!f.loginName || !f.loginName.trim()) {
        this.$message.warning('请输入主账号登录名')
        return
      }
      if (/[\u4e00-\u9fa5]/.test(f.loginName)) {
        this.$message.warning('账号请不要用中文')
        return
      }
      if (!f.initPassword || !/^(?=.*[a-z])(?=.*\d).{6,}$/.test(f.initPassword)) {
        this.$message.warning('密码至少要有数字和小写字母，并且长度至少6位')
        return
      }
      this.addSaving = true
      postAction(STORE_PREFIX, {
        name: f.name.trim(),
        storeType: f.storeType,
        bossName: f.bossName.trim(),
        loginName: f.loginName.trim(),
        // 密码前端 md5 后传输（与登录/注册/改密同惯例，禁明文过网），后端识别 32 位 hex 直存
        initPassword: md5(f.initPassword)
      }).then(res => {
        if (res.code === 200) {
          this.$message.success(res.message || '新增成功，已生成独立租户与主账号')
          this.addVisible = false
          // 新增成功直接进入编辑页继续完善门店信息（res.data = addV2 返回的新门店 id）
          if (res.data) {
            this.$router.push({ path: STORE_INFO_PATH, query: { id: res.data } })
          } else {
            this.loadData(1)
          }
        } else {
          this.$message.warning(res.message || '新增失败')
        }
      }).finally(() => {
        this.addSaving = false
      })
    },
    // 编辑：跳门店信息页三 Tab（query.id 为 jsh_store id）
    handleEdit(record) {
      this.$router.push({ path: STORE_INFO_PATH, query: { id: record.id } })
    },
    // ===== 进入后台（门店身份切换）：一次性授权码换临时会话，备份总店后注入门店身份 =====
    initProxyMode() {
      try {
        const raw = localStorage.getItem('dining-proxy-backup')
        if (raw) {
          const backup = JSON.parse(raw)
          if (backup && backup.hqToken) {
            this.proxyMode = true
            this.proxyStoreName = backup.storeName || ''
          }
        }
      } catch (e) { /* 非法备份忽略 */ }
    },
    handleEnterBackend(record) {
      const that = this
      const storeName = record.storeName || record.name
      this.$confirm({
        title: '进入门店后台',
        content: `将以「${storeName}」管理员身份进入后台，当前总店登录将临时切换为门店身份；可从 门店管理 页面横幅「返回总店」。`,
        onOk() {
          return postAction(`${STORE_PREFIX}/${record.id}/proxy-code`).then(res => {
            if (res.code !== 200 || !res.data || !res.data.code) {
              that.$message.warning(res.message || '授权失败')
              return
            }
            return postAction(`${STORE_PREFIX}/proxy-login`, { code: res.data.code }).then(login => {
              if (login.code !== 200 || !login.data || !login.data.token) {
                that.$message.warning(login.message || '进入失败')
                return
              }
              // 备份总店会话（原样存 localStorage 值，返回时原样恢复）→ 注入门店身份 → 进后台
              const backup = {
                hqToken: localStorage.getItem('pro__Access-Token'),
                hqUserId: localStorage.getItem('pro__Login_userId'),
                hqUserinfo: localStorage.getItem('pro__Login_Userinfo'),
                storeName: login.data.storeName || storeName
              }
              localStorage.setItem('dining-proxy-backup', JSON.stringify(backup))
              localStorage.setItem('pro__Access-Token', JSON.stringify({ value: login.data.token, expire: null }))
              localStorage.setItem('pro__Login_userId', JSON.stringify({ value: String(login.data.userId), expire: null }))
              // 同步切换 USER_INFO 的租户为门店租户（request.js 的 X-Tenant-Id 头取自该值，防止门店身份下仍发总店租户）
              that.switchUserinfoTenant(login.data.token)
              window.location.href = '/'
            })
          })
        }
      })
    },
    // 把 USER_INFO 的租户切换为 token 后缀租户（门店身份期间 X-Tenant-Id 头随门店；异常时跳过，后端 token 解析兜底）
    switchUserinfoTenant(token) {
      try {
        const raw = localStorage.getItem('pro__Login_Userinfo')
        if (!raw) return
        const wrapped = JSON.parse(raw)
        if (wrapped && wrapped.value) {
          wrapped.value.tenantId = String(token).split('_').pop()
          localStorage.setItem('pro__Login_Userinfo', JSON.stringify(wrapped))
        }
      } catch (e) { /* 格式异常忽略 */ }
    },
    // 返回总店：恢复备份会话并清除门店身份标记
    handleBackToHq() {
      try {
        const raw = localStorage.getItem('dining-proxy-backup')
        const backup = raw ? JSON.parse(raw) : null
        if (backup && backup.hqToken) {
          localStorage.setItem('pro__Access-Token', backup.hqToken)
          localStorage.setItem('pro__Login_userId', backup.hqUserId)
          if (backup.hqUserinfo) {
            localStorage.setItem('pro__Login_Userinfo', backup.hqUserinfo)
          }
        }
        localStorage.removeItem('dining-proxy-backup')
      } catch (e) { /* 非法备份忽略 */ }
      window.location.href = '/plugin/dining/store'
    },
    // 商品模式弹窗
    handleGoodsModel(record) {
      this.$refs.goodsModelModal.show(record)
    },
    // 删除（软删→回收站，确认文案照抄云贝）
    handleDelete(record) {
      const that = this
      this.$confirm({
        title: '删除确认',
        content: '你确定要删除当前门店吗？一旦删除后，门店数据将会清除，请谨慎操作！',
        onOk() {
          return deleteAction(`${STORE_PREFIX}/${record.id}`).then(res => {
            if (res.code === 200) {
              that.$message.success(res.message || '删除成功')
              that.loadData()
            } else {
              that.$message.warning(res.message)
            }
          })
        }
      })
    },
    // 恢复（回收站，确认文案照抄云贝）
    handleRestore(record) {
      const that = this
      this.$confirm({
        title: '恢复确认',
        content: '你确定要恢复当前门店吗？',
        onOk() {
          return postAction(`${STORE_PREFIX}/${record.id}/restore`).then(res => {
            if (res.code === 200) {
              that.$message.success(res.message || '恢复成功')
              that.loadData()
            } else {
              that.$message.warning(res.message)
            }
          })
        }
      })
    },
    // 彻底删除（回收站，确认文案照抄云贝）
    handlePurge(record) {
      const that = this
      this.$confirm({
        title: '彻底删除确认',
        content: '你确定要彻底删除当前门店吗？一旦删除后，门店数据将会清除，请谨慎操作！',
        onOk() {
          return deleteAction(`${STORE_PREFIX}/${record.id}/purge`).then(res => {
            if (res.code === 200) {
              that.$message.success(res.message || '删除成功')
              that.loadData()
            } else {
              that.$message.warning(res.message)
            }
          })
        }
      })
    }
  }
}
</script>
<style scoped>
@import '~@assets/less/common.less';
.field-tip {
  color: #999;
  font-size: 12px;
}
.add-form {
  max-width: 460px;
}
/* 门店后台模式横幅 */
.proxy-banner {
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 12px;
  color: #8c6d1f;
}
.proxy-back {
  margin-left: 16px;
  font-weight: bold;
}
</style>
